import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Euro, Lock, ScrollText, ShieldCheck } from "lucide-react";
import type { LeadData } from "./types";

type CountryCode = {
  value: string;
  flagCode: string;
};

const COUNTRY_CODES: CountryCode[] = [
  { value: "+49", flagCode: "de" },
  { value: "+41", flagCode: "ch" },
  { value: "+43", flagCode: "at" },
  { value: "+30", flagCode: "gr" },
  { value: "+31", flagCode: "nl" },
  { value: "+32", flagCode: "be" },
  { value: "+33", flagCode: "fr" },
  { value: "+34", flagCode: "es" },
  { value: "+39", flagCode: "it" },
  { value: "+44", flagCode: "gb" },
  { value: "+1", flagCode: "us" },
];

type LeadFormProps = {
  onSubmit?: (lead: LeadData) => void;
  title?: string;
  description?: string;
  buttonLabel?: string;
  idPrefix?: string;
  enableAgentModal?: boolean;
};

export function LeadForm({
  onSubmit,
  title = "Kostenlose Sofort-Fallprüfung",
  description = "Schildern Sie uns Ihren Fall diskret und unverbindlich.",
  buttonLabel = "Kostenlose Erstprüfung starten",
  idPrefix = "lead",
  enableAgentModal = false,
}: LeadFormProps) {
  const [form, setForm] = useState<LeadData>({
    fullName: "",
    email: "",
    phone: "",
    betrugsart: "",
    damageAmount: "",
    message: "",
  });
  const [countryCode, setCountryCode] = useState("+49");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // success handled by global event that shows a thank-you page

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    // client-side validation
    const newErrors: Record<string, string> = {};
    if (!form.fullName || !form.fullName.trim()) newErrors.fullName = "Bitte geben Sie Ihren Namen an.";
    if (!form.email || !form.email.trim()) newErrors.email = "Bitte geben Sie Ihre E-Mail-Adresse an.";
    else {
      // basic email format check
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!re.test(form.email)) newErrors.email = "Bitte geben Sie eine gültige E‑Mail an.";
    }
    if (!form.phone || !form.phone.trim()) newErrors.phone = "Bitte geben Sie Ihre Telefonnummer an.";
    if (!form.betrugsart || !form.betrugsart.trim()) newErrors.betrugsart = "Bitte wählen Sie die Art des Betrugs.";
    if (!form.message || !form.message.trim()) newErrors.message = "Bitte beschreiben Sie kurz Ihren Fall.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);
    try {
      const res = await fetch("https://api.veritasberatung.com/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.fullName,
          email: form.email,
          phone: `${countryCode} ${form.phone}`.trim(),
          countryCode,
          betrugsart: form.betrugsart,
          damageAmount: form.damageAmount,
          message: form.message,
        }),
      });

      if (!res.ok) {
        console.error("Sending failed", await res.text());
        setSubmitting(false);
        return;
      }

      if (enableAgentModal) {
        onSubmit?.(form);
      } else {
        // dispatch a global event so the app can show a thank-you page
        const payload = {
          fullName: form.fullName,
          email: form.email,
          phone: `${countryCode} ${form.phone}`.trim(),
          countryCode,
          betrugsart: form.betrugsart,
          damageAmount: form.damageAmount,
          message: form.message,
        };
        // save to sessionStorage and navigate to a dedicated thank-you page
        try {
          sessionStorage.setItem("leadData", JSON.stringify(payload));
          // clear form state
          setForm({ fullName: "", email: "", phone: "", betrugsart: "", damageAmount: "", message: "" });
          setCountryCode("+49");
          window.location.href = "/thank-you";
        } catch (err) {
          console.error("Could not save lead data", err);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl bg-card p-6 text-card-foreground shadow-[var(--shadow-elegant)] md:p-8">
      <div className="mb-6 text-center">
        <h3 className="text-xl font-bold">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="space-y-4">
        {/* success handled by landing page via `leadSubmitted` event */}
        <div>
          <Label htmlFor={`${idPrefix}-fullName`} className="text-xs uppercase tracking-wide text-muted-foreground">
            Vollständiger Name
          </Label>
          <Input
            id={`${idPrefix}-fullName`}
            required
            placeholder="Erika Mustermann"
            className="mt-1.5"
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          />
          {errors.fullName ? <p className="mt-1 text-xs text-destructive">{errors.fullName}</p> : null}
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-email`} className="text-xs uppercase tracking-wide text-muted-foreground">
            E-Mail Adresse
          </Label>
          <Input
            id={`${idPrefix}-email`}
            type="email"
            required
            placeholder="name@veritasberatung.com"
            className="mt-1.5"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          {errors.email ? <p className="mt-1 text-xs text-destructive">{errors.email}</p> : null}
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-phone`} className="text-xs uppercase tracking-wide text-muted-foreground">
            Telefonnummer
          </Label>
          <div className="mt-1.5 flex gap-2">
            <Select value={countryCode} onValueChange={setCountryCode}>
              <SelectTrigger className="h-11 w-[84px] shrink-0 rounded-md border-border bg-card px-2 text-sm shadow-none sm:w-[92px]">
                <SelectValue placeholder="" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_CODES.slice(0, 3).map((code) => (
                  <SelectItem key={code.value} value={code.value}>
                    <span className="flex items-center gap-2">
                      <img
                        src={`https://flagcdn.com/w20/${code.flagCode}.png`}
                        alt=""
                        aria-hidden="true"
                        className="h-4 w-5 rounded-[2px] object-cover"
                      />
                      <span>{code.value}</span>
                    </span>
                  </SelectItem>
                ))}
                {COUNTRY_CODES.length > 3 ? <div className="my-1 h-px bg-muted" /> : null}
                {COUNTRY_CODES.slice(3).map((code) => (
                  <SelectItem key={code.value} value={code.value}>
                    <span className="flex items-center gap-2">
                      <img
                        src={`https://flagcdn.com/w20/${code.flagCode}.png`}
                        alt=""
                        aria-hidden="true"
                        className="h-4 w-5 rounded-[2px] object-cover"
                      />
                      <span>{code.value}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id={`${idPrefix}-phone`}
              placeholder="123 4567890"
              className="h-11 flex-1"
              inputMode="numeric"
              autoComplete="tel-national"
              value={form.phone}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
                setForm({ ...form, phone: digitsOnly });
              }}
            />
            {errors.phone ? <p className="mt-1 text-xs text-destructive">{errors.phone}</p> : null}
          </div>
        </div>

        <div>
          <Label htmlFor={`${idPrefix}-betrugsart`} className="text-xs uppercase tracking-wide text-muted-foreground">
            Art des Betrugs
          </Label>
          <Select
            value={form.betrugsart}
            onValueChange={(value) => setForm({ ...form, betrugsart: value })}
          >
            <SelectTrigger className="mt-1.5 h-11 w-full rounded-md border-border bg-card px-3 text-sm shadow-none">
              <SelectValue placeholder="Bitte auswählen..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Investment Betrug">Investment Betrug</SelectItem>
              <SelectItem value="Forex Betrug">Forex Betrug</SelectItem>
              <SelectItem value="Trading Betrug">Trading Betrug</SelectItem>
              <SelectItem value="Krypto Betrug">Krypto Betrug</SelectItem>
              <SelectItem value="Festgeld Betrug">Festgeld Betrug</SelectItem>
              <SelectItem value="Romance Scam">Romance Scam</SelectItem>
              <SelectItem value="Anderer Betrug">Anderer Betrug</SelectItem>
            </SelectContent>
          </Select>
          {errors.betrugsart ? <p className="mt-1 text-xs text-destructive">{errors.betrugsart}</p> : null}
        </div>

        <div>
          <Label htmlFor={`${idPrefix}-damageAmount`} className="text-xs uppercase tracking-wide text-muted-foreground">
            Betrag (ungefähr)
          </Label>
          <div className="relative mt-1.5">
            <Input
              id={`${idPrefix}-damageAmount`}
              placeholder="z.B. 10.000"
              className="pr-10"
              inputMode="decimal"
              autoComplete="off"
              value={form.damageAmount}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[^0-9.]/g, "");
                const parts = sanitized.split(".");
                const normalized = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : sanitized;
                setForm({ ...form, damageAmount: normalized });
              }}
            />
            <Euro className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-message`} className="text-xs uppercase tracking-wide text-muted-foreground">
            Beschreibung *
          </Label>
          <Textarea
            id={`${idPrefix}-message`}
            rows={4}
            placeholder="Beschreiben Sie kurz, was passiert ist..."
            className="mt-1.5 resize-none"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          {errors.message ? <p className="mt-1 text-xs text-destructive">{errors.message}</p> : null}
        </div>
        <Button type="submit" variant="gold" className="w-full" size="lg" disabled={submitting}>
          <ScrollText className="h-4 w-4" /> {submitting ? "Sende..." : buttonLabel}
        </Button>
      </div>

      <div className="mt-5 flex items-center justify-center gap-5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><Lock className="h-3 w-3" /> SSL verschlüsselt</span>
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> DSGVO konform</span>
      </div>
    </form>
  );
}